import type { Bill, Movement, Offer, Product } from '@sense/shared';
import { api } from './api';
import { db, getMeta, setMeta, type LedgerRow } from './db';
import { renew, useSession } from './session';
import { useCart } from './cart';

/**
 * Moving between phone-only Starter and a paid (cloud) plan.
 *
 * Up: everything kept on the phone goes to the server once. Products and offers made on the phone
 * have negative ids, so they are uploaded first and every reference (bills, stock moves, offers,
 * stock, batches, held bills, the open cart) is rewritten to the server's ids before the normal
 * sync uploads bills, stock moves, customers and shifts. Khata payments go last, once their
 * customers exist on the server. Each step deletes its outbox rows, so an interrupted upgrade
 * picks up where it stopped (meta 'cloudUpgradePending', retried on start).
 *
 * Down: the outbox is emptied first, so nothing is left behind on the server side.
 */

let running: Promise<boolean> | null = null;

export function upgradeToCloud(): Promise<boolean> {
  running ??= run().finally(() => { running = null; });
  return running;
}

async function run(): Promise<boolean> {
  await setMeta('cloudUpgradePending', true);
  try {
    // New token: the old one still says Starter, and the server refuses Starter uploads.
    if (!(await renew()) && !(await useSession.getState().refresh())) return false;
    await useSession.getState().loadMe();
    if (useSession.getState().me?.tenant.plan_id === 'starter') { await setMeta('cloudUpgradePending', false); return false; }

    await uploadProducts();
    await uploadOffers();
    await uploadStore();
    const { syncNow, pullCatalog, pullStock, pullCustomers } = await import('./sync');
    await syncNow();
    await uploadLedger();
    await pullCatalog(true);
    await pullStock();
    await pullCustomers();
    await setMeta('cloudUpgradePending', false);
    return true;
  } catch {
    return false; // stays pending; retried on the next start
  }
}

/**
 * On start: finish an upgrade that was cut short, and let a phone-only store notice it was upgraded
 * (paid on the web, or set by support). One small call a day at most.
 */
export async function checkCloudUpgrade() {
  if (await getMeta<boolean>('cloudUpgradePending', false)) { void upgradeToCloud(); return; }
  const me = useSession.getState().me;
  if (me?.tenant.plan_id !== 'starter' || !navigator.onLine) return;
  if (Date.now() - (await getMeta<number>('planCheckedAt', 0)) < 86400_000) return;
  await setMeta('planCheckedAt', Date.now());
  const r = await api<{ plan_id: string }>('/api/plan').catch(() => null);
  if (r && r.plan_id !== 'starter') void upgradeToCloud();
}

async function uploadProducts() {
  const rows = await db.outbox.where('kind').equals('product').toArray();
  if (!rows.length) return;
  const map = new Map<number, number>();
  for (let i = 0; i < rows.length; i += 500) {
    const chunk = rows.slice(i, i + 500);
    const r = await api<{ ids: Array<{ id: number; sku: string }> }>('/api/products', { method: 'POST', json: chunk.map((row) => { const { id: _id, version: _v, ...p } = row.doc as Product; return p; }) });
    const bySku = new Map(r.ids.map((x) => [x.sku, x.id]));
    for (const row of chunk) {
      const p = row.doc as Product;
      const id = bySku.get(p.sku);
      if (id != null && p.id < 0) map.set(p.id, id);
    }
    await remapProducts(map);
    await db.outbox.bulkDelete(chunk.map((row) => row.seq!));
  }
}

const pid = (map: Map<number, number>, id: number) => map.get(id) ?? id;

async function remapProducts(map: Map<number, number>) {
  if (!map.size) return;
  await db.transaction('rw', [db.outbox, db.bills, db.offers, db.stock, db.batches, db.held, db.products], async () => {
    await db.outbox.toCollection().modify((row) => {
      if (row.kind === 'bill') for (const l of (row.doc as Bill).items) l[0] = pid(map, l[0]);
      else if (row.kind === 'movement') {
        const m = row.doc as Movement;
        for (const l of m.lines) l[0] = pid(map, l[0]);
        for (const e of m.meta?.expiry ?? []) e[0] = pid(map, e[0]);
      } else if (row.kind === 'offer') {
        const o = row.doc as Offer;
        if (o.products) o.products = o.products.map((x) => pid(map, x));
      }
    });
    await db.bills.toCollection().modify((b) => { for (const l of b.items) l[0] = pid(map, l[0]); });
    await db.offers.toCollection().modify((o) => { if (o.products) o.products = o.products.map((x) => pid(map, x)); });
    for (const [from, to] of map) {
      const s = await db.stock.get(from);
      if (s) { await db.stock.delete(from); await db.stock.put({ pid: to, qty: s.qty }); }
      await db.products.delete(from);
    }
    await db.batches.toCollection().modify((b) => { b.product_id = pid(map, b.product_id); });
    await db.held.toCollection().modify((h) => { for (const l of h.lines) l.pid = pid(map, l.pid); });
  });
  const cart = useCart.getState();
  if (cart.lines.some((l) => map.has(l.pid))) useCart.setState({ lines: cart.lines.map((l) => ({ ...l, pid: pid(map, l.pid) })) });
}

async function uploadOffers() {
  const rows = await db.outbox.where('kind').equals('offer').toArray();
  const map = new Map<number, number>();
  for (const row of rows) {
    const { id, ...o } = row.doc as Offer;
    const r = await api<{ id: number }>('/api/offers', { method: 'POST', json: id > 0 ? { id, ...o } : o });
    if (id < 0) map.set(id, r.id);
    await db.outbox.delete(row.seq!);
  }
  if (!map.size) return;
  // Bills remember which offer gave the discount.
  await db.transaction('rw', [db.outbox, db.bills, db.offers], async () => {
    await db.outbox.where('kind').equals('bill').modify((row) => { const b = row.doc as Bill; if (b.offers) b.offers = b.offers.map(([o, a]) => [map.get(o) ?? o, a]); });
    await db.bills.toCollection().modify((b) => { if (b.offers) b.offers = b.offers.map(([o, a]) => [map.get(o) ?? o, a]); });
    await db.offers.bulkDelete([...map.keys()]);
  });
}

async function uploadStore() {
  const rows = await db.outbox.where('kind').equals('store').toArray();
  const last = rows.at(-1);
  if (last) await api('/api/store', { method: 'PUT', json: last.doc });
  if (rows.length) await db.outbox.bulkDelete(rows.map((r) => r.seq!));
}

async function uploadLedger() {
  for (const row of await db.outbox.where('kind').equals('ledger').toArray()) {
    const l = row.doc as LedgerRow;
    await api(`/api/customers/${l.customer_id}/ledger`, { method: 'POST', json: { kind: l.kind, amount: l.amount, method: l.method ?? undefined, note: l.note ?? undefined } });
    await db.outbox.delete(row.seq!);
  }
}

/**
 * Down to Starter: upload whatever is still waiting while the plan is paid, then switch. From then
 * on this phone keeps its data and stops syncing.
 */
export async function moveToStarter() {
  const { syncNow } = await import('./sync');
  await syncNow();
  const waiting = await db.outbox.count();
  if (waiting) throw new Error(`${waiting} ${waiting === 1 ? 'item is' : 'items are'} still waiting to sync. Connect to the internet and try again.`);
  await api('/api/plan/choose', { method: 'POST', json: { plan: 'starter' } });
  await renew();
  await useSession.getState().loadMe();
  await setMeta('phoneSeeded', true); // the phone already has the store's data
}
